import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    ArrowUpRight,
    Facebook,
    Instagram,
    Linkedin
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { Wordmark } from '@/components/generals/wordmark'

import {
    clientAreaLabel,
    company,
    menuNav,
    phonesByCity,
    siteConfig
} from '@/constants/site'
import { services } from '@/constants/home'

const socialLinks = [
    { label: 'Instagram', href: company.socials.instagram, icon: Instagram },
    { label: 'LinkedIn', href: company.socials.linkedin, icon: Linkedin },
    { label: 'Facebook', href: company.socials.facebook, icon: Facebook }
]

const FooterHeading: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => {
    return (
        <h2 className={cn('font-mono text-[10px] uppercase tracking-[0.18em] text-white/45', className)}>
            {children}
        </h2>
    )
}

export const Footer: FC = (): ReactNode => {
    return (
        <footer data-header-theme='dark' className='bg-ink text-white'>
            <Container className='pt-20 md:pt-28'>
                <div className='grid gap-14 border-b border-white/10 pb-16 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-5'>
                        <Link href='/' aria-label={`${siteConfig.name}, página inicial`} className='inline-flex'>
                            <Wordmark />
                        </Link>

                        <p className='mt-10 font-heading text-[1.75rem] uppercase leading-[1.08] tracking-[-0.02em] md:text-[2.6rem]'>
                            Mais Negócios,
                            <span className='block text-white/45'>
                                menos Preocupação.
                            </span>
                        </p>

                        <div className='mt-10'>
                            <NewsletterForm />
                        </div>

                        <ul className='mt-8 flex gap-3'>
                            {socialLinks.map(({ label, href, icon: Icon }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={`${siteConfig.name} no ${label}`}
                                        className='flex size-11 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-white hover:bg-white hover:text-ink'
                                    >
                                        <Icon aria-hidden='true' className='size-4' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='grid gap-12 sm:grid-cols-3 sm:gap-8 lg:col-span-7'>
                        <nav aria-label='Rodapé'>
                            <FooterHeading>Empresa</FooterHeading>

                            <ul className='mt-5 space-y-3 text-sm'>
                                {menuNav.map((item) => (
                                    <li key={item.href}>
                                        <Link href={item.href} className='text-white/75 transition-colors hover:text-white'>
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}

                                <li>
                                    <a
                                        href={company.clientAreaUrl}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-white/75 transition-colors hover:text-white'
                                    >
                                        {clientAreaLabel}
                                    </a>
                                </li>
                            </ul>
                        </nav>

                        <div>
                            <FooterHeading>Serviços</FooterHeading>

                            <ul className='mt-5 space-y-3 text-sm'>
                                {services.map((service) => (
                                    <li key={service.slug}>
                                        <Link href={`/servicos#${service.slug}`} className='text-white/75 transition-colors hover:text-white'>
                                            {service.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <FooterHeading>Sede</FooterHeading>

                            <address className='mt-5 text-sm not-italic leading-relaxed text-white/75'>
                                {company.address.street}
                                <br />
                                {company.address.locality}
                                <br />
                                {company.address.country}
                            </address>

                            <a
                                href={company.mapsUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-brand-soft transition-colors hover:text-white'
                            >
                                Como chegar
                                <ArrowUpRight aria-hidden='true' className='size-3' />
                            </a>

                            <FooterHeading className='mt-10'>Fale conosco</FooterHeading>

                            <dl className='mt-5 space-y-4 text-sm'>
                                <div>
                                    <dt className='text-white/45'>WhatsApp</dt>
                                    <dd className='mt-1'>
                                        <a
                                            href={company.whatsappUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='transition-colors hover:text-brand-soft'
                                        >
                                            (21) 3907-7544
                                        </a>
                                    </dd>
                                </div>

                                <div>
                                    <dt className='text-white/45'>Simulação</dt>
                                    <dd className='mt-1'>
                                        <a
                                            href={company.simulationUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='transition-colors hover:text-brand-soft'
                                        >
                                            Faça uma simulação
                                        </a>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>

                <div className='flex flex-col gap-4 border-b border-white/10 py-8 lg:flex-row lg:items-center lg:gap-10'>
                    <p className='shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-white/45'>
                        Atendimento
                    </p>

                    <ul className='flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.12em] text-white/80'>
                        {phonesByCity.map(({ city, lines }) => (
                            <li key={city} className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                                <span aria-hidden='true' className='size-1 bg-highlight' />
                                <span className='text-white/50'>{city}</span>
                                {lines.map((line, index) => (
                                    <span key={line.href} className='flex items-center gap-3'>
                                        {index > 0 && <span aria-hidden='true' className='text-white/30'>/</span>}
                                        <a href={line.href} className='transition-colors hover:text-highlight'>
                                            {line.label}
                                        </a>
                                    </span>
                                ))}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='flex flex-col gap-2 py-8 font-mono text-[10px] uppercase tracking-[0.14em] text-white/45 sm:flex-row sm:justify-between'>
                    <p>
                        © 2026 {company.legalName}. Todos os direitos reservados.
                    </p>

                    <p>CNPJ: {company.cnpj}</p>
                </div>
            </Container>
        </footer>
    )
}
